/**
 * Seedance 参考素材时长探测与浏览器内裁剪。
 *
 * Kinovi 约束：参考视频时长总和 ≤15s，参考音频时长总和 ≤15s（各自独立）。
 * - probeDuration / probeBlobDuration：通过媒体元素 metadata 读取时长
 *   （跨域 URL 读 metadata 不需要 CORS）
 * - trimAudioFile：AudioContext 解码 → 切片 → MediaRecorder 编码
 *   （audio/mp4 优先、audio/webm 次之、WAV 兜底）
 * - trimVideoFile：播放捕获（captureStream + MediaRecorder）重编码，
 *   mp4 优先（Chrome 126+/Safari），否则 webm；音轨经 AudioContext
 *   静默路由，裁剪过程无外放声音
 */

export const TYPE_DURATION_LIMIT = 15; // 秒，视频/音频各自总和上限

// 上传素材单文件大小上限（Kinovi 约束）：视频 ≤50MB，图片/音频 ≤20MB。
// 请求生成 API 前必须满足；视频可裁剪后再判（裁短会重编码、体积随之变小）。
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
export const MAX_IMAGE_AUDIO_BYTES = 20 * 1024 * 1024;
export function assetSizeLimit(format: string): number {
  return format === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_AUDIO_BYTES;
}
export const bytesToMB = (n: number) => Math.round((n / (1024 * 1024)) * 10) / 10;

const durationCache = new Map<string, number>();

/**
 * 强制走浏览器下载（fetch → blob → a[download]）。
 * 跨域 URL 直接挂 download 属性会被浏览器忽略而变成页面跳转；
 * 转成 blob 对象后 download 生效。CORS 拒绝时退回新标签页打开。
 */
export async function downloadMediaFile(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // 延迟回收，确保下载已接管
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  } catch {
    window.open(url, '_blank');
  }
}

function probeFromSrc(src: string, format: 'video' | 'audio'): Promise<number> {
  return new Promise((resolve, reject) => {
    const el = document.createElement(format === 'video' ? 'video' : 'audio');
    el.preload = 'metadata';
    (el as HTMLMediaElement).muted = true;
    let settled = false;
    const cleanup = () => {
      el.removeAttribute('src');
      el.load();
    };
    const done = (d: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      if (Number.isFinite(d) && d > 0) resolve(d);
      else reject(new Error('Duration unavailable'));
    };
    // 兜底超时：seek 兜底路径万一不触发事件也不至于卡死导入
    const timer = setTimeout(() => done(el.duration), 10000);
    el.onloadedmetadata = () => {
      const d = el.duration;
      // 部分 mp4（非 fast-start / moov 无时长，RedGifs 常见）loadedmetadata 时 duration=Infinity，
      // seek 到极大值强制浏览器算出真实时长（timeupdate/seeked 后 duration 变有限值）。
      if (Number.isFinite(d) && d > 0) {
        done(d);
        return;
      }
      const onSeek = () => {
        el.ontimeupdate = null;
        el.onseeked = null;
        done(el.duration);
      };
      el.ontimeupdate = onSeek;
      el.onseeked = onSeek;
      try {
        el.currentTime = 1e101;
      } catch {
        done(el.duration);
      }
    };
    el.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      reject(new Error('Failed to load media metadata'));
    };
    el.src = src;
  });
}

/* ---------- mp4 时长：range 请求直读 moov/mvhd ---------- */

const MP4_LIKE = /\.(mp4|m4v|m4a|mov)(?:[?#]|$)/i;

/** 取 URL 的一小段字节；失败/不支持 range 时返回 null */
async function readRange(url: string, start: number, length: number): Promise<Uint8Array | null> {
  try {
    const res = await fetch(url, {
      headers: { Range: `bytes=${start}-${start + length - 1}` },
    });
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    return buf.byteLength ? new Uint8Array(buf) : null;
  } catch {
    return null;
  }
}

/** 从一段 buffer 里找 mvhd 并算出时长（秒）；找不到返回 0 */
function parseMvhdDuration(buf: Uint8Array): number {
  const v = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  for (let i = 0; i + 36 <= buf.length; i++) {
    // "mvhd"
    if (buf[i] !== 0x6d || buf[i + 1] !== 0x76 || buf[i + 2] !== 0x68 || buf[i + 3] !== 0x64) continue;
    // version(1) + flags(3) 之后：v0 = creation/modification 各 4 字节，
    // v1 = 各 8 字节；timescale 与 duration 紧随其后
    const version = buf[i + 4];
    const timescale = version === 0 ? v.getUint32(i + 16) : v.getUint32(i + 24);
    const duration =
      version === 0 ? v.getUint32(i + 20) : v.getUint32(i + 28) * 2 ** 32 + v.getUint32(i + 32);
    if (timescale <= 0) return 0;
    const secs = duration / timescale;
    // 合理性校验，避免撞上 mdat 里恰好等于 "mvhd" 的数据
    return secs > 0 && secs < 86_400 ? secs : 0;
  }
  return 0;
}

/**
 * 顺着 mp4 顶层 atom 表跳到 moov 读时长。
 *
 * <video> 元素对**非 fast-start** 的 mp4（moov 在文件尾部）经常探测超时 ——
 * 浏览器得先把 mdat 拖到尾巴才拿得到元数据。这里靠 atom 头里的 size 字段
 * 直接跳过 mdat，只花几个十几字节的 range 请求就能读到 moov/mvhd，
 * 完全不下载媒体数据。
 */
async function probeMp4Duration(url: string): Promise<number> {
  let offset = 0;
  for (let hop = 0; hop < 24; hop++) {
    const head = await readRange(url, offset, 16);
    if (!head || head.length < 8) return 0;
    const v = new DataView(head.buffer, head.byteOffset, head.byteLength);
    let size = v.getUint32(0);
    const type = String.fromCharCode(head[4], head[5], head[6], head[7]);
    let headerSize = 8;
    if (size === 1) {
      // size==1 表示真实长度是随后的 64 位整数（大文件的 mdat 常见）
      if (head.length < 16) return 0;
      size = v.getUint32(8) * 2 ** 32 + v.getUint32(12);
      headerSize = 16;
    }
    if (type === 'moov') {
      // mvhd 基本恒为 moov 的第一个子 atom，取头部 2KB 足够
      const moov = await readRange(url, offset, Math.min(size, 2048));
      return moov ? parseMvhdDuration(moov) : 0;
    }
    // size==0 表示该 atom 延伸到文件尾，其后不会再有 moov
    if (size < headerSize) return 0;
    offset += size;
  }
  return 0;
}

/**
 * URL 素材时长（秒），带缓存；探测失败返回 0（不阻塞用户，交给后端兜底）。
 *
 * 两条探测路径并跑，谁先给出正数就用谁：
 *   - <video>/<audio> 元素：通吃各种容器格式，fast-start 的文件很快
 *   - mp4 range 解析：非 fast-start（moov 在文件尾）时元素探测会超时，
 *     靠它兜底，只读十几字节的 atom 头
 *
 * 失败**不写缓存** —— 一旦把 0 永久缓存下来，后续每次算价都会当成
 * 「时长未知」落到上限兜底价，且再也不会自愈。只缓存 >0 的成功结果。
 */
export async function probeDuration(url: string, format: 'video' | 'audio'): Promise<number> {
  const cached = durationCache.get(url);
  if (cached !== undefined) return cached;

  const attempts: Promise<number>[] = [probeFromSrc(url, format).catch(() => 0)];
  if (MP4_LIKE.test(url)) attempts.push(probeMp4Duration(url).catch(() => 0));

  const d = await new Promise<number>((resolve) => {
    let pending = attempts.length;
    for (const p of attempts) {
      void p.then((secs) => {
        if (secs > 0) resolve(secs);
        else if (--pending === 0) resolve(0);
      });
    }
  });
  if (d > 0) durationCache.set(url, d);
  return d;
}

/** 本地文件时长（秒）；探测失败返回 0 */
export async function probeBlobDuration(blob: Blob, format: 'video' | 'audio'): Promise<number> {
  const url = URL.createObjectURL(blob);
  try {
    return await probeFromSrc(url, format);
  } catch {
    return 0;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** 一组素材 URL 的时长总和 */
export async function sumDurations(urls: string[], format: 'video' | 'audio'): Promise<number> {
  const list = await Promise.all(urls.map((u) => probeDuration(u, format)));
  return list.reduce((s, d) => s + d, 0);
}

/** 已探到的时长（秒）；从未成功探测过则返回 undefined */
export function knownDuration(url: string): number | undefined {
  const d = durationCache.get(url);
  return d !== undefined && d > 0 ? d : undefined;
}

/**
 * 全部素材都已探到时长时同步求和；只要有一个缺失就返回 undefined。
 * 计价用它先走同步路径，拿不到再退回异步探测 —— 避免「探测中」被当成
 * 「时长未知」而按上限兜底报价（那会把价格虚高好几倍）。
 *
 * 素材在本仓库里就是一串 URL（没有随素材携带的 durationSeconds 字段），
 * 加入素材时的预算校验已经探过一次并写进 durationCache，这里直接读缓存。
 */
export function knownSumDurations(urls: string[]): number | undefined {
  if (urls.length === 0) return 0;
  let total = 0;
  for (const u of urls) {
    const d = knownDuration(u);
    if (d === undefined) return undefined;
    total += d;
  }
  return total;
}

/* ---------- 音频裁剪：decode → slice → 编码 ---------- */

function encodeWav(channels: Float32Array[], sampleRate: number): Blob {
  const numCh = channels.length;
  const numFrames = channels[0]?.length ?? 0;
  const bytesPerSample = 2;
  const blockAlign = numCh * bytesPerSample;
  const dataSize = numFrames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let ch = 0; ch < numCh; ch++) {
      const v = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(offset, v < 0 ? v * 0x8000 : v * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * 音频裁剪。输出格式按 Kinovi 生产实证选择：
 * m4a（audio/mp4）> opus（audio/webm）> WAV 兜底。
 * 前两种走 MediaRecorder 实时录制（≤15s，报进度），WAV 走即时编码。
 */
export async function trimAudioFile(
  blob: Blob,
  start: number,
  end: number,
  baseName: string,
  onProgress?: (ratio: number) => void
): Promise<File> {
  const arrayBuffer = await blob.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(arrayBuffer);
    const sr = decoded.sampleRate;
    const s = Math.max(0, Math.floor(start * sr));
    const e = Math.min(decoded.length, Math.floor(end * sr));
    const numCh = decoded.numberOfChannels;
    const stem = baseName.replace(/\.[^.]+$/, '');

    const mime = MediaRecorder.isTypeSupported('audio/mp4')
      ? 'audio/mp4'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : null;

    if (!mime) {
      // 兜底：即时编码 WAV
      const channels: Float32Array[] = [];
      for (let ch = 0; ch < numCh; ch++) {
        channels.push(decoded.getChannelData(ch).slice(s, e));
      }
      const wav = encodeWav(channels, sr);
      return new File([wav], `${stem}-trimmed.wav`, { type: 'audio/wav' });
    }

    // 切片装进新 AudioBuffer，静默实时播放并录制
    const clip = ctx.createBuffer(numCh, e - s, sr);
    for (let ch = 0; ch < numCh; ch++) {
      clip.copyToChannel(decoded.getChannelData(ch).slice(s, e), ch);
    }
    const source = ctx.createBufferSource();
    source.buffer = clip;
    const dest = ctx.createMediaStreamDestination();
    source.connect(dest); // 不连 ctx.destination → 无外放
    const recorder = new MediaRecorder(dest.stream, { mimeType: mime });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) chunks.push(ev.data);
    };
    const done = new Promise<Blob>((resolve, reject) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
      recorder.onerror = () => reject(new Error('Recording failed'));
    });
    const clipSecs = (e - s) / sr;
    const startedAt = performance.now();
    const progressTimer = onProgress
      ? window.setInterval(() => {
          onProgress(Math.min(1, (performance.now() - startedAt) / 1000 / clipSecs));
        }, 200)
      : null;
    recorder.start(250);
    source.start();
    await new Promise<void>((resolve) => {
      source.onended = () => resolve();
    });
    recorder.stop();
    const out = await done;
    if (progressTimer !== null) window.clearInterval(progressTimer);
    const ext = mime === 'audio/mp4' ? 'm4a' : 'webm';
    return new File([out], `${stem}-trimmed.${ext}`, { type: mime });
  } finally {
    await ctx.close().catch(() => {});
  }
}

/* ---------- 视频裁剪：播放捕获重编码 ---------- */

export async function trimVideoFile(
  blob: Blob,
  start: number,
  end: number,
  baseName: string,
  onProgress?: (ratio: number) => void
): Promise<File> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.src = url;
  video.playsInline = true;
  const actx = new AudioContext();
  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Failed to load video'));
    });
    video.currentTime = start;
    await new Promise<void>((resolve) => {
      video.onseeked = () => resolve();
    });

    // 音轨走 AudioContext 静默路由（不连 destination → 不外放，
    // 但 dest.stream 里有完整音频；直接 muted 会导致捕获到静音）
    const srcNode = actx.createMediaElementSource(video);
    const dest = actx.createMediaStreamDestination();
    srcNode.connect(dest);
    const captured = (
      video as HTMLVideoElement & { captureStream: () => MediaStream }
    ).captureStream();
    const stream = new MediaStream([
      ...captured.getVideoTracks(),
      ...dest.stream.getAudioTracks(),
    ]);

    const mime = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm';
    const recorder = new MediaRecorder(stream, {
      mimeType: mime,
      videoBitsPerSecond: 8_000_000,
    });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    const done = new Promise<Blob>((resolve, reject) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
      recorder.onerror = () => reject(new Error('Recording failed'));
    });

    recorder.start(250);
    await video.play();
    await new Promise<void>((resolve) => {
      const tick = () => {
        const ratio = Math.min(1, (video.currentTime - start) / Math.max(0.1, end - start));
        onProgress?.(ratio);
        if (video.currentTime >= end || video.ended) {
          resolve();
        } else {
          requestAnimationFrame(tick);
        }
      };
      tick();
    });
    video.pause();
    recorder.stop();
    const out = await done;
    const ext = mime === 'video/mp4' ? 'mp4' : 'webm';
    return new File([out], `${baseName.replace(/\.[^.]+$/, '')}-trimmed.${ext}`, {
      type: mime,
    });
  } finally {
    URL.revokeObjectURL(url);
    await actx.close().catch(() => {});
  }
}
